import moment from "moment";
import { useSelector } from "react-redux";
import { RolePageNames } from "@finpay-development/shared-platform/lib/esm/security/model/role-page-names";
import { checkPagePermission } from "@finpay-development/shared-platform/lib/esm/security/components/access-control";
import { RootState } from "../../state/root-reducer";
import {
  patientIocResult,
  patientSearchResult,
  patientSearchSource,
} from "@finpay-development/shared-platform/lib/esm/admissions-advisor/models/patient";
import { VobPostBody } from "@finpay-development/shared-platform/lib/esm/admissions-advisor/models/vob";
import { PfrEstimator } from "@finpay-development/shared-platform/lib/esm/admissions-advisor/models/estimator";

/** V3 documents carry patientId; legacy V2 documents carry fpPatientId until re-indexed. */
export const getPatientId = (source: patientSearchSource): number | undefined =>
  source.patientId || source.fpPatientId || undefined;

export const getFullName = (source: patientSearchSource): string =>
  `${source.firstName ?? ""} ${source.lastName ?? ""}`.trim();

export const getInitials = (source: patientSearchSource): string => {
  const first = (source.firstName ?? "").trim().charAt(0);
  const last = (source.lastName ?? "").trim().charAt(0);
  return `${first}${last}`.toUpperCase() || "?";
};

export const formatDate = (value?: string | null): string =>
  value ? moment(value).format("l") : "";

export const formatDateTime = (value?: string | null): string =>
  value ? moment(value).format("l h:mm a") : "";

/** "DOB · Patient ID · Client" line shown under the patient name; missing values are omitted. */
export const getPatientMeta = (
  source: patientSearchSource,
  clientNamesById: Map<number, string>
): string => {
  const parts: string[] = [];
  const dob = formatDate(source.birthDate);
  if (dob) parts.push(`DOB: ${dob}`);
  const patientId = getPatientId(source);
  if (patientId) parts.push(`Patient ID: ${patientId}`);
  const clients = getPatientClientNames(source, clientNamesById);
  if (clients.length > 0) parts.push(`Client: ${clients.join(", ")}`);
  return parts.join(" · ");
};

export const getPatientVobs = (source: patientSearchSource): VobPostBody[] =>
  source.vobs ?? [];

export const getPatientIocs = (source: patientSearchSource): patientIocResult[] =>
  source.iocs ?? [];

/** Estimates live on each VOB in the search document. */
export const getPatientEstimates = (source: patientSearchSource): PfrEstimator[] =>
  getPatientVobs(source).reduce<PfrEstimator[]>(
    (acc, vob) => acc.concat(vob.estimates ?? []),
    []
  );

export const describeVob = (vob: VobPostBody): string => {
  const body = vob.vobBody;
  const parts: string[] = [];
  if (body?.selfPay) parts.push("Self Pay");
  else if (body?.payer?.payorName) parts.push(body.payer.payorName);
  if (body?.facility?.facilityName) parts.push(body.facility.facilityName);
  const created = formatDateTime(vob.createDt);
  if (created) parts.push(`Created ${created}`);
  return parts.join(" · ");
};

export const describeEstimate = (estimate: PfrEstimator): string => {
  const body = estimate.estimateBody;
  const parts: string[] = [];
  const locNames = (body?.selectedLevelsOfCare ?? [])
    .map((loc) => loc?.facilityLevelOfCareName)
    .filter((name): name is string => Boolean(name));
  if (locNames.length > 0) parts.push(locNames.join(", "));
  else if (body?.facility?.facilityName) parts.push(body.facility.facilityName);
  const created = formatDateTime(estimate.createDt);
  if (created) parts.push(`Created ${created}`);
  return parts.join(" · ");
};

export const describeIoc = (ioc: patientIocResult): string => {
  const parts: string[] = [];
  if (ioc.clientName) parts.push(ioc.clientName);
  if (ioc.facilityName) parts.push(ioc.facilityName);
  if (ioc.iocStatus) parts.push(ioc.iocStatus);
  const created = formatDateTime(ioc.createDt);
  if (created) parts.push(`Created ${created}`);
  return parts.join(" · ");
};

const toClientId = (value: unknown): number | undefined => {
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : undefined;
};

const getVobClientId = (vob: VobPostBody): number | undefined =>
  toClientId(vob.fpClientId) ?? toClientId(vob.vobBody?.client?.clientId);

/** Every client a search document is linked to: the patient, associated clients, IOCs, and VOBs. */
export const getPatientClientIds = (source: patientSearchSource): Set<number> => {
  const ids = new Set<number>();
  const add = (value: unknown) => {
    const id = toClientId(value);
    if (id !== undefined) ids.add(id);
  };
  add(source.clientId);
  (source.associatedClients ?? []).forEach((c) => add(c.clientId));
  getPatientIocs(source).forEach((ioc) => add(ioc.clientId));
  getPatientVobs(source).forEach((vob) => add(getVobClientId(vob)));
  return ids;
};

/** Names for every linked client, preferring the app's client list over names stored on the document. */
export const getPatientClientNames = (
  source: patientSearchSource,
  clientNamesById: Map<number, string>
): string[] => {
  const documentNames = new Map<number, string>();
  const addName = (id: unknown, name?: string) => {
    const clientId = toClientId(id);
    if (clientId !== undefined && name && !documentNames.has(clientId)) {
      documentNames.set(clientId, name);
    }
  };
  (source.associatedClients ?? []).forEach((c) => addName(c.clientId, c.clientName));
  getPatientIocs(source).forEach((ioc) => addName(ioc.clientId, ioc.clientName));
  getPatientVobs(source).forEach((vob) =>
    addName(getVobClientId(vob), vob.vobBody?.client?.clientName)
  );

  return Array.from(getPatientClientIds(source))
    .map((id) => clientNamesById.get(id) || documentNames.get(id))
    .filter((name): name is string => Boolean(name));
};

export const patientMatchesClients = (
  patient: patientSearchResult,
  clientIds: Set<number>
): boolean => {
  if (clientIds.size === 0) return true;
  const patientClientIds = getPatientClientIds(patient._source);
  return Array.from(clientIds).some((id) => patientClientIds.has(id));
};

/** Copy of the patient whose IOCs, VOBs, and estimates are limited to the given clients. */
export const narrowPatientToClients = (
  patient: patientSearchResult,
  clientIds: Set<number>
): patientSearchResult => {
  if (clientIds.size === 0) return patient;
  const source = patient._source;
  const inClients = (value: unknown) => {
    const id = toClientId(value);
    return id !== undefined && clientIds.has(id);
  };
  return {
    ...patient,
    _source: {
      ...source,
      iocs: getPatientIocs(source).filter((ioc) => inClients(ioc.clientId)),
      vobs: getPatientVobs(source)
        .filter((vob) => inClients(getVobClientId(vob)))
        .map((vob) => ({
          ...vob,
          estimates: (vob.estimates ?? []).filter((est) => {
            const estClientId = toClientId(est.estimateBody?.client?.clientId);
            return estClientId === undefined || clientIds.has(estClientId);
          }),
        })),
    },
  };
};

/** Highest id in the list, i.e. the most recently created record. */
export const latestId = <T>(items: T[], getId: (item: T) => number | undefined): number | undefined =>
  items.reduce<number | undefined>((max, item) => {
    const id = getId(item);
    return id !== undefined && (max === undefined || id > max) ? id : max;
  }, undefined);

export const patientRecordPath = (patientId: number, encounterId?: number): string => {
  const base = `/specialist/dashboard/${patientId}`;
  return encounterId ? `${base}?encounter=${encounterId}` : base;
};

export const vobPath = (patientId: number, vobId: number): string =>
  `/patients/${patientId}/vob/${vobId}`;

export const estimatePath = (patientId: number, estimateId: number): string =>
  `/patients/${patientId}/estimate/${estimateId}`;

export const hubPath = (patientId: number): string => `/hub/patient/${patientId}`;

export interface GlobalSearchAccess {
  /** FinAdvisor read or edit: patient record, VOBs, estimates, IOCs. */
  finAdvisor: boolean;
  /** Payment Hub edit, matching the header Payment Hub button. */
  paymentHub: boolean;
  /** Whether the search is shown at all: FinAdvisor read/edit or Payment Hub edit. */
  any: boolean;
}

export const useGlobalSearchAccess = (): GlobalSearchAccess => {
  const userRolePages = useSelector(
    (state: RootState) => state.userContext.userProfile.userRole.userRolePages
  );
  // empty userRolePages = super user, matching checkPermissions in AccessControl
  const isSuperUser = userRolePages.length === 0;
  const has = (rolePageName: RolePageNames, permitReadOnly: boolean) =>
    isSuperUser || checkPagePermission({ userRolePages, rolePageName, permitReadOnly });

  const finAdvisor = has(RolePageNames.Estimates, true);
  const paymentHub = has(RolePageNames.PaymentHub, false);
  return { finAdvisor, paymentHub, any: finAdvisor || paymentHub };
};
